import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle, ShieldCheck, X } from "@phosphor-icons/react";

/**
 * One-time AI processing consent modal. UK GDPR requires explicit, informed
 * consent when sending personal data (CV bullets, job-ad text, cover letters)
 * to a third-party LLM provider. We show this the first time the user runs
 * any AI feature in their browser.
 *
 * Consent is stored in localStorage so a logged-out user can still see it on
 * any device they sign in on for the first time. Server-side consent log is
 * captured separately at signup as `marketing_opt_in` + ToS acceptance.
 */
const STORAGE_KEY = "jv_ai_consent_v1";

export function hasAIConsent() {
  try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
}

export default function AIConsentModal({ onConsent, onCancel }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(!hasAIConsent());
  }, []);

  const accept = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
    setVisible(false);
    onConsent?.();
  };

  const decline = () => {
    setVisible(false);
    onCancel?.();
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--brand-ink)]/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="ai-consent-modal"
    >
      <div className="card-hard rounded-md bg-white max-w-lg w-full p-6 relative">
        <button
          onClick={decline}
          aria-label="close consent dialog"
          className="absolute top-3 right-3 w-8 h-8 rounded hover:bg-[var(--brand-bg)] flex items-center justify-center"
          data-testid="ai-consent-close"
        >
          <X size={16} weight="bold"/>
        </button>

        <div className="w-11 h-11 rounded-md bg-[var(--brand-bg)] border-[1.5px] border-[var(--brand-ink)] flex items-center justify-center mb-4">
          <Sparkle size={22} weight="fill" className="text-[var(--brand-primary)]"/>
        </div>

        <p className="label-tiny mb-1">/ai consent · one-time</p>
        <h2 className="font-display text-2xl font-semibold tracking-tight mb-3">
          quick heads-up before the AI helps.
        </h2>

        <p className="text-sm leading-relaxed mb-4">
          Vault Coach (CV Tailor, Cover Letter Checker, Interview Prep) and the Culture Barometer send the text you&apos;re working with to <b>Anthropic Claude</b> via JobVault&apos;s API key. Here&apos;s exactly what that means:
        </p>

        <ul className="text-sm space-y-2 mb-5 list-disc pl-5 leading-relaxed">
          <li><b>What we send:</b> the specific bullets / job ad / cover letter you&apos;re actively working with — nothing from your wider vault.</li>
          <li><b>Who Anthropic shares it with:</b> nobody. Anthropic doesn&apos;t train models on API data (as per their <a className="underline" href="https://www.anthropic.com/legal/aup" target="_blank" rel="noreferrer">Acceptable Use Policy</a>).</li>
          <li><b>How long it&apos;s retained:</b> Anthropic logs API calls for up to 30 days for abuse-monitoring, then deletes. We store the resulting AI output in your vault — only you can read it.</li>
          <li><b>You stay in control:</b> the AI output is guidance, not advice. You decide what to submit to employers.</li>
        </ul>

        <div className="card-hard rounded-md bg-[var(--pastel-mint)] p-3 mb-5 flex items-start gap-2">
          <ShieldCheck size={18} weight="fill" className="shrink-0 mt-0.5"/>
          <p className="text-xs leading-relaxed">
            You can withdraw this consent any time by clicking &ldquo;reset AI consent&rdquo; in Settings. After withdrawal, AI features will ask again next time.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={accept}
            data-testid="ai-consent-accept"
            className="flex-1 btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-3 text-sm font-medium"
          >
            sounds fair, continue
          </button>
          <button
            onClick={decline}
            data-testid="ai-consent-decline"
            className="btn-hard bg-white text-[var(--brand-ink)] rounded-md px-4 py-3 text-sm font-medium"
          >
            not now
          </button>
        </div>

        <p className="text-xs font-mono-d text-[var(--brand-muted)] mt-4">
          Read the full{" "}
          <Link to="/privacy" className="underline" target="_blank">privacy policy</Link>{" "}
          or{" "}
          <Link to="/terms" className="underline" target="_blank">terms of service</Link>{" "}
          for the details.
        </p>
      </div>
    </div>
  );
}
